import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLine, entryConnector, moduleSides, obstacleRowOn, standardLanes, straightExit, type CollectibleRun } from '../segmentBuilders';
import {
  SegmentKind,
  type CeilingZone,
  type CollectibleSpawnPoint,
  type FixedObstaclePlacement,
  type ObstacleVariant,
  type SegmentDefinition,
} from '../SegmentDefinition';

/**
 * SEG_06 – scaffold passage 40 m (design/01_segments/SEG_06_Scaffold_Passage; the sheet's 30 m
 * standardised to 40 m). A scaffold deck spans the passage; two striped crossbars (1.6 m clearance)
 * divide it into the sheet's bays. In between, hand-made layouts mix what stands in a real scaffold
 * passage – scaffold tubes, pallets, cable drums and big bags to jump, low scaffold frames to slide
 * under, material stacks that close a lane – so the run through is never one long slide (user
 * 2026-10-02; before: six crossbars in two slide chains).
 *
 *   Deck        z 5–38, underside 3.9 m – soft (limits the double jump, never a crash). A jump fits under
 *               it (head 3.3 m), double-jump obstacles (brick stack, big cable reel) do not.
 *   Crossbars   B08, B29 over all lanes (slide); a material stack under a bar closes its lane. A bar is a
 *               0.3 m striped box beam (1.6–1.9 m) with 2 m to the deck: its top is walkable – a double jump
 *               that bumps the deck (feet 2.1 m) passes over it (SEGMENT_STANDARD "Sprungehrlichkeit")
 *   Rows        R15 · R22  7 m rhythm with the bars: jump ↔ slide in one lane (no lane change at top speed)
 *               R18        10 m from both bars: one lane change in time at top speed
 *               R36        7 m after the second bar
 *   Layouts     sparse (warm-up tier): one obstacle row between the bars · dense (from tier 2): combos –
 *               jump then slide, lane change into a slide, slalom between material stacks
 *   Bottles     every layout names its risk line: through its combos (arcs over the jumps, under the bars)
 *
 * Fairness beyond the SequenceValidator (start and top speed): one lane never asks for two jumps closer
 * than a jump carries at top speed, tall jumps (cable drum, big bag) only stand where the run-up is free –
 * tests/unit/scaffold.test.ts runs the real PlayerController through every layout.
 */
export const SCAFFOLD = {
  length: 40,
  deck: { zFrom: 5, zTo: 38, clearance: 3.9 },
  /** Striped box beam: `depth` along the track, `height` from the underside (ENV_ScaffoldPassage_A BAR). */
  bar: { clearance: 1.6, depth: 0.3, height: 0.3 },
  /** Crossbars over all lanes – row name → z (material stacks may stand under them). */
  bars: { B08: 8, B29: 29 },
  /** Obstacle rows between and after the crossbars – row name → z. */
  rows: { R15: 15, R18: 18, R22: 22, R36: 36 },
  /** Bars and deck span the track and the curbs (scaffold standards stand beside the barriers). */
  halfSpan: WORLD_CONFIG.trackWidth / 2 + 0.15,
  /** Bottle lines along the module: first / last bottle, spacing (m). */
  bottles: { from: 1, to: 39, spacing: 2 },
  /** Slalom line: leaves the centre lane after the first crossbar, is back before the second (z). */
  slalom: { out: 11, back: 27 },
} as const;

type BarRow = keyof typeof SCAFFOLD.bars;
type ObstacleRow = keyof typeof SCAFFOLD.rows;

/**
 * What stands in a scaffold passage (sheet SEG_06: "Gerüststange (unten)", "Palette", "Querstrebe
 * (niedrig)", material stored under the scaffold; cable drum and big bag for the masonry work).
 */
const OBS = {
  /**
   * Row of orange traffic cones, 0.66 m – jump. Was the low scaffold tubes (OBS_SCAFFOLD_POLE_A, 0.66 m), replaced by the
   * orange cones (user 2026-10-04: the thin tubes are hard to see at speed and at night).
   */
  tube: 'OBS_CONE_ROW_LOW_A',
  /** Four Euro pallets, 0.58 m – jump. */
  pallets: 'OBS_PALLET_A',
  /** Small cable drum, 0.9 m – jump (only with a free run-up: R18). */
  drum: 'OBS_CABLE_DRUM_A',
  /** Big bag of sand, 1.1 m – jump (only with a free run-up: R18). */
  bag: 'OBS_BIG_BAG_A',
  /** Low scaffold frame with striped crossbar, 1.6 m – slide (its standards stand on the lane edges). */
  frame: 'OBS_SCAFFOLD_PASS_A',
  /** Pallets and formwork blocks stacked up to the crossbar – lane change. */
  stack: 'OBS_MATERIAL_STACK_A',
} as const;

type ObstacleId = (typeof OBS)[keyof typeof OBS];
/** Obstacle per lane (left, centre, right), null = free. */
type LaneSet = readonly [ObstacleId | null, ObstacleId | null, ObstacleId | null];
/** Material stacks under the crossbars, obstacles in the rows between them. */
type Layout = Partial<Record<BarRow, LaneSet> & Record<ObstacleRow, LaneSet>>;

interface LayoutSpec {
  readonly id: string;
  readonly weight: number;
  readonly rowLayout: NonNullable<ObstacleVariant['rowLayout']>;
  readonly rows: Layout;
  /** Bottle lines through the layout's risk line. */
  readonly lines: readonly string[];
}

const LANES = Array.from({ length: WORLD_CONFIG.laneCount }, (_, lane) => lane);
const BAR_TYPES = [ObstacleType.LaneChange] as const;
const ROW_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;

const laneLine = (lane: number) => `LANE_L${lane}`;
/** Slalom line out to `side` around the material stack of R18. */
const slalomLine = (side: number) => `SLALOM_L${side}`;
const mirrorLane = (lane: number) => WORLD_CONFIG.laneCount - 1 - lane;
/** Line names end in the lane they lead through (LANE_L0, SLALOM_L2) – mirrored with the layout. */
const mirrorLine = (line: string) => line.replace(/_L(\d)$/, (_match, lane: string) => `_L${mirrorLane(Number(lane))}`);

function placements(layout: Layout): FixedObstaclePlacement[] {
  return Object.entries(layout).flatMap(([row, lanes]) =>
    (lanes ?? []).flatMap((obstacleId, lane) => (obstacleId === null ? [] : [{ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId }])),
  );
}

function variant(spec: LayoutSpec): ObstacleVariant {
  return { id: spec.id, weight: spec.weight, rowLayout: spec.rowLayout, placements: placements(spec.rows), collectibleLines: spec.lines };
}

/** The same layout seen from the other side (left ↔ right), with its risk line. */
function mirrored(spec: LayoutSpec, id: string): LayoutSpec {
  const rows = Object.fromEntries(Object.entries(spec.rows).map(([row, lanes]) => [row, lanes && [lanes[2], lanes[1], lanes[0]]])) as Layout;
  return { ...spec, id, rows, lines: spec.lines.map(mirrorLine) };
}

// ── Layouts ────────────────────────────────────────────────────────────────
// Lanes left → right. B08/B29 are the crossbars (slide in every lane not closed by a stack).

/** Warm-up: slide, a scaffold tube in the centre (jump or dodge), slide, pallets in the outer lanes. */
const ENTRY_TUBE: LayoutSpec = {
  id: 'ENTRY_TUBE',
  weight: 1,
  rowLayout: 'sparse',
  rows: { R18: [null, OBS.tube, null], R36: [OBS.pallets, null, OBS.pallets] },
  lines: [laneLine(1)],
};

/** Warm-up: material under the first bar on the left (change lane into the slide), cable drum on the right. */
const ENTRY_STORAGE_LEFT: LayoutSpec = {
  id: 'ENTRY_STORAGE_LEFT',
  weight: 0.5,
  rowLayout: 'sparse',
  rows: { B08: [OBS.stack, null, null], R18: [null, null, OBS.drum], R36: [null, OBS.tube, null] },
  lines: [laneLine(2), laneLine(1)],
};

/**
 * Alternating heights (sheet: "Abwechselnde Höhen"): tube | frame | pallets, then frame | tube | frame –
 * every lane jumps and slides in turn; the centre runs slide – jump – slide in 7 m steps.
 */
const ALTERNATING_HEIGHTS: LayoutSpec = {
  id: 'ALTERNATING_HEIGHTS',
  weight: 1,
  rowLayout: 'dense',
  rows: {
    R15: [OBS.tube, OBS.frame, OBS.pallets],
    R22: [OBS.frame, OBS.tube, OBS.frame],
    R36: [OBS.tube, null, OBS.pallets],
  },
  lines: [laneLine(1)],
};

/**
 * Material stored down the centre from the first bar to R22 (change lane into the slide); the outer
 * lanes alternate: left jump then slide, right slide then jump.
 */
const CENTRE_STORAGE: LayoutSpec = {
  id: 'CENTRE_STORAGE',
  weight: 1,
  rowLayout: 'dense',
  rows: {
    B08: [null, OBS.stack, null],
    R15: [OBS.pallets, OBS.stack, OBS.frame],
    R22: [OBS.frame, OBS.stack, OBS.tube],
    R36: [OBS.tube, null, OBS.pallets],
  },
  lines: [laneLine(0), laneLine(2)],
};

/**
 * Slalom between material stacks: left closed under the first bar, centre at R18, right under the second
 * bar. Safe line: left under the frame. Risk line: out to the right over the cable drum, back to the
 * centre into the slide.
 */
const SLALOM_RIGHT: LayoutSpec = {
  id: 'SLALOM_RIGHT',
  weight: 0.5,
  rowLayout: 'dense',
  rows: {
    B08: [OBS.stack, null, null],
    R18: [OBS.frame, OBS.stack, OBS.drum],
    B29: [null, null, OBS.stack],
    R36: [OBS.pallets, null, OBS.stack],
  },
  lines: [slalomLine(2)],
};

/** Bottleneck: stacks close both outer lanes at R18 – into the centre and over the big bag, tubes and pallets at the exit. */
const BOTTLENECK: LayoutSpec = {
  id: 'BOTTLENECK',
  weight: 1,
  rowLayout: 'dense',
  rows: {
    R18: [OBS.stack, OBS.bag, OBS.stack],
    R36: [OBS.tube, OBS.pallets, OBS.tube],
  },
  lines: [laneLine(1)],
};

export const SCAFFOLD_LAYOUTS: readonly LayoutSpec[] = [
  ENTRY_TUBE,
  ENTRY_STORAGE_LEFT,
  mirrored(ENTRY_STORAGE_LEFT, 'ENTRY_STORAGE_RIGHT'),
  ALTERNATING_HEIGHTS,
  CENTRE_STORAGE,
  SLALOM_RIGHT,
  mirrored(SLALOM_RIGHT, 'SLALOM_LEFT'),
  BOTTLENECK,
];

// ── Terrain, sockets, bottles ──────────────────────────────────────────────

const ceilings: CeilingZone[] = [
  {
    id: 'CEIL_DECK',
    zFrom: SCAFFOLD.deck.zFrom,
    zTo: SCAFFOLD.deck.zTo,
    xFrom: -SCAFFOLD.halfSpan,
    xTo: SCAFFOLD.halfSpan,
    clearance: SCAFFOLD.deck.clearance,
    soft: true,
  },
  ...Object.entries(SCAFFOLD.bars).map(([row, z]) => ({
    id: `CEIL_BAR_${row}`,
    zFrom: z - SCAFFOLD.bar.depth / 2,
    zTo: z + SCAFFOLD.bar.depth / 2,
    xFrom: -SCAFFOLD.halfSpan,
    xTo: SCAFFOLD.halfSpan,
    clearance: SCAFFOLD.bar.clearance,
    top: SCAFFOLD.bar.clearance + SCAFFOLD.bar.height,
  })),
];

const floating = () => COLLECTIBLE_FLOAT_HEIGHT;
const { from, to, spacing } = SCAFFOLD.bottles;
const CENTRE = Math.floor(WORLD_CONFIG.laneCount / 2);
const SLALOM_SIDES = [0, WORLD_CONFIG.laneCount - 1] as const;

/** Bottle line whose socket ids carry the line name – lines share lanes and z (a lane line and a slalom). */
function bottleLine(name: string, runs: readonly Omit<CollectibleRun, 'spacing' | 'height'>[]): CollectibleSpawnPoint[] {
  return collectibleLine(
    WORLD_CONFIG,
    name,
    runs.map((run) => ({ ...run, spacing, height: floating })),
  ).map((point) => ({ ...point, id: `${point.id}_${name}` }));
}

const collectibleSpawnPoints: CollectibleSpawnPoint[] = [
  ...LANES.flatMap((lane) => bottleLine(laneLine(lane), [{ lane, from, to }])),
  ...SLALOM_SIDES.flatMap((side) =>
    bottleLine(slalomLine(side), [
      { lane: CENTRE, from, to: SCAFFOLD.slalom.out - spacing },
      { lane: side, from: SCAFFOLD.slalom.out, to: SCAFFOLD.slalom.back - spacing },
      { lane: CENTRE, from: SCAFFOLD.slalom.back, to },
    ]),
  ),
];

export const SEG_SCAFFOLD_40M: SegmentDefinition = {
  id: 'SEG_SCAFFOLD_40M',
  kind: SegmentKind.Straight,
  displayName: 'Gerüstpassage 40 m',
  length: SCAFFOLD.length,
  width: WORLD_CONFIG.trackWidth,
  // Blender overlay (ENV_ScaffoldPassage_A) on two base road modules.
  visualAssetId: 'SEG_SCAFFOLD_40M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 0 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
  ],

  entry: entryConnector(),
  exits: [straightExit(SCAFFOLD.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: { surfaces: [], gaps: [], ceilings },

  obstacleSpawnPoints: [
    ...Object.entries(SCAFFOLD.bars).flatMap(([row, z]) => obstacleRowOn(WORLD_CONFIG, row, z, LANES, BAR_TYPES)),
    ...Object.entries(SCAFFOLD.rows).flatMap(([row, z]) => obstacleRowOn(WORLD_CONFIG, row, z, LANES, ROW_TYPES)),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: SCAFFOLD_LAYOUTS.map(variant),

  collectibleSpawnPoints,

  ...moduleSides(SCAFFOLD.length),

  generation: {
    weight: 0.25,
    minDistance: 150,  // was 250 – from 2026-10-05 the dense tier starts at 200 m: its warm-up layouts still get 150 … 200 m
    minSegmentsAfterJunction: 1,
    family: 'SCAFFOLD',
    tags: ['scaffold', 'terrain', 'slide', 'jump'],
  },
};
